// Threshold waivers. Pure. No React, no Three.js.
//
// ── Codex (Dragonlord's Lair) ──
//   "Dragons cast to this site require no threshold and cost (1) less."
//
// The cost half is a plain cells-filtered MODIFY_CAST_COST ledger
// entry. The threshold half is THIS module: a declarative static
// WAIVE_THRESHOLD entry (BLOCK_EDGE_TRAVERSAL pattern. It never
// "fires" through dispatchSilentOp). The affordability + placement
// pipelines ask:
//   • does ANY in-play waiver match this candidate? → the card is
//     castable from hand even when its thresholds are unmet
//   • WHICH cells carry a matching waiver? → when thresholds are
//     unmet, the legal spawn set is restricted to exactly those cells
//
// Thresholds are GATES, not consumed (codex). A waiver removes the
// gate for casts landing on the waiver source's cell, nothing else.

import { OP } from '../schema';
import { matchesFilter } from '../cardFilter';
import { isCardSilenced } from '../../core/keywordQuery';
import { isDisabled } from '../scope';

/**
 * Scan the live board for WAIVE_THRESHOLD static entries.
 *
 * @param meshes   Map<id, mesh>. Mesh.userData.cardInstance + position
 * @param cellAtWorld (x, z) => { col, row } | null
 * @param getFullCard (cardId) => catalog card (carries .automation)
 * @returns [{ cell: {col,row}, filter }]
 */
export function collectThresholdWaivers(meshes, cellAtWorld, getFullCard) {
  const out = [];
  if (!meshes) return out;
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card || isCardSilenced(card) || isDisabled(card)) continue;
    const entries = getFullCard?.(card.cardId)?.automation || [];
    for (const entry of entries) {
      if (entry.trigger !== 'static') continue;
      for (const eff of (entry.effect || [])) {
        if (eff.op !== OP.WAIVE_THRESHOLD) continue;
        const cell = cellAtWorld?.(mesh.position.x, mesh.position.z);
        if (!cell) continue;
        out.push({ cell: { col: cell.col, row: cell.row }, filter: eff.args?.filter || {} });
      }
    }
  }
  return out;
}

/** Cells (as 'col,row' keys) where `candidate`'s threshold is waived. */
export function thresholdWaiverCellsFor(waivers, candidate) {
  const out = new Set();
  for (const w of (waivers || [])) {
    if (!matchesFilter(candidate, w.filter)) continue;
    out.add(`${w.cell.col},${w.cell.row}`);
  }
  return out;
}

/** True iff a matching waiver sits exactly at `cell`. */
export function isThresholdWaivedAt(waivers, candidate, cell) {
  if (!cell) return false;
  return thresholdWaiverCellsFor(waivers, candidate).has(`${cell.col},${cell.row}`);
}

/**
 * Threshold gate for a cast landing on a KNOWN cell: satisfied
 * outright by the player's resources, or forgiven by a matching
 * waiver on exactly that cell. Every effect-driven cast with a fixed
 * destination (Cradle of Etherrum's cast-from-top) routes through
 * this so waiver sources (Dragonlord's Lair) apply by default.
 */
export function thresholdsMetAt(resources, candidate, thresholds, cell, waivers) {
  const met = ['air', 'earth', 'fire', 'water']
    .every((el) => (resources?.[el] || 0) >= (thresholds?.[el] || 0));
  if (met) return true;
  return isThresholdWaivedAt(waivers, candidate, cell);
}
